import { Fragment, useState } from 'react'

import { cn, formatMoney } from 'utils'
import { type MemberOnboardingMedicalPlanDisplayItem, type QuotingQuoteResult } from 'utils/web_api.schemas'

import { useSelfServeExplorerContext } from './QuoteSelfServeExplorerContext'
import { CheckIconList } from '../../../components/reusable/DataDisplayComponents'

interface DetailsTabContentProps {
  employee: QuotingQuoteResult
  plans?: MemberOnboardingMedicalPlanDisplayItem[]
}

const SpendBreakdownVisual = (
  {
    allowanceAmount,
    planCost,
    targetPlan,
    containerWidthPercentage = 100
  }:
  {
    allowanceAmount: number,
    planCost: number,
    targetPlan: MemberOnboardingMedicalPlanDisplayItem,
    containerWidthPercentage?: number
  }
) => {
  const allowanceFullyCoversPremium = allowanceAmount >= planCost
  // if the allowance fully covers the premium, we want to display the premium cost as a percentage of the allowance
  // otherwise, we want to display the allowance as a percentage of the premium cost
  const employerContributionOrPremiumCostPercentage = allowanceFullyCoversPremium
    ? ((planCost / allowanceAmount) * 100)
    : ((allowanceAmount / planCost) * 100)
  const employeeContributionOrQmePercentage = 100 - employerContributionOrPremiumCostPercentage
  const hasQmeAvailable = allowanceAmount - planCost > 0

  const [hoveredSection, setHoveredSection] = useState<'premium' | 'qme' | null>(null)

  return (
    <>
      {/* Bar visualization */}
      <div style={{ width: `${containerWidthPercentage}%` }}>
        {/* If the employee contribution is less than 20%, add the marker above the bar to prevent the markers from overlapping */}
        {employeeContributionOrQmePercentage < 20 && (
          <div
            className='flex justify-end'
            onMouseEnter={() => setHoveredSection('qme')}
            onMouseLeave={() => setHoveredSection(null)}
          >
            <div className={cn('body-sm cursor-default text-right pb-6 pr-2 transition-all duration-300 border-r border-gray-400', hoveredSection === 'qme' && 'scale-105')}>
              <span className='text-subdued block'>{allowanceFullyCoversPremium ? 'Other expenses' : 'Employee contribution'}</span>
              <span className='text-main'>{formatMoney(Math.abs(allowanceAmount - planCost))}</span>
            </div>
          </div>
        )}
        <div className='h-8 flex items-center justify-between gap-1'>
          <div
            className={cn('h-full shadow-md bg-thatch-300 opacity-85 rounded-lg transition-all duration-300', hoveredSection === 'premium' && 'scale-[1.01] opacity-100 shadow-lg')}
            style={{ width: `${employerContributionOrPremiumCostPercentage}%` }}
            onMouseEnter={() => setHoveredSection('premium')}
            onMouseLeave={() => setHoveredSection(null)}
          />
          <div
            className={cn('h-full shadow-md opacity-85 rounded-lg transition-all duration-300',
              allowanceFullyCoversPremium ? 'bg-thatch-800' : 'bg-red-500',
              hoveredSection === 'qme' && 'scale-[1.01] opacity-100 shadow-lg'
            )}
            style={{ width: `${employeeContributionOrQmePercentage}%` }}
            onMouseEnter={() => setHoveredSection('qme')}
            onMouseLeave={() => setHoveredSection(null)}
          />
        </div>
        <div className='flex items-center justify-between gap-1'>
          <div
            className='h-full border-r border-gray-400 flex items-end justify-end'
            style={{ width: `${employerContributionOrPremiumCostPercentage}%` }}
            onMouseEnter={() => setHoveredSection('premium')}
            onMouseLeave={() => setHoveredSection(null)}
          >
            <div className={cn('body-sm cursor-default text-right pr-2 pt-6 pb-1 transition-all duration-300', hoveredSection === 'premium' && 'scale-105')}>
              <span className='text-subdued block'>
                {allowanceFullyCoversPremium ? 'Health Insurance' : 'Employer contribution'}
              </span>
              <span className='text-main'>{allowanceFullyCoversPremium ? formatMoney(planCost) : formatMoney(allowanceAmount)}</span>
            </div>
          </div>
          {employeeContributionOrQmePercentage >= 20
            ? (
              <div
                className='h-full border-r border-gray-400 flex items-end justify-end'
                style={{ width: `${employeeContributionOrQmePercentage}%` }}
                onMouseEnter={() => setHoveredSection('qme')}
                onMouseLeave={() => setHoveredSection(null)}
              >
                <div className={cn('body-sm cursor-default text-right pr-2 pt-6 pb-1 transition-all duration-300', hoveredSection === 'qme' && 'scale-105')}>
                  <span className='text-subdued block'>{allowanceFullyCoversPremium ? 'Other expenses' : 'Employee contribution'}</span>
                  <span className='text-main'>{formatMoney(Math.abs(allowanceAmount - planCost))}</span>
                </div>
              </div>
              )
            : (
              <div
                className='h-full flex items-end justify-end'
                style={{ width: `${employeeContributionOrQmePercentage}%` }}
                onMouseEnter={() => setHoveredSection('qme')}
                onMouseLeave={() => setHoveredSection(null)}
              />
              )}
        </div>
      </div>
      {/* Breakdown explanation */}
      <div className='mt-4'>
        <CheckIconList items={[
          <Fragment key='plan-description'>
            <span>Can purchase a</span>
            <span className='font-bold capitalize'>
              {' '}
              {(targetPlan.plan.metal_tier[0].toUpperCase() + targetPlan.plan.metal_tier.slice(1)).replace('Expanded_', '')}
              {' '}
              {targetPlan.plan.plan_type.toUpperCase()}
            </span>
            <span>{' '}plan for <span className='font-bold'>{formatMoney(targetPlan.total_premium.subunits / 100)}</span> {allowanceFullyCoversPremium ? 'without any out of pocket spend.' : <>while covering <span className='font-bold'>{formatMoney(Math.abs(allowanceAmount - planCost))}</span> from their own pocket.</>}</span>
          </Fragment>,
          hasQmeAvailable && (
            <Fragment key='qme-description'>
              <span className='font-bold'>{formatMoney(allowanceAmount - planCost)}</span>
              <span>
                {' '}per month to spend on other qualified medical expenses including:
                <br /> <br />
                Co-pays, prescriptions • Lab tests • Eyeglasses • Hearing aids • Dermatology • Mental health
              </span>
            </Fragment>
          )
        ].filter(Boolean)}
        />
      </div>
    </>
  )
}

export const EmployeeDetailsTab = ({ employee, plans }: DetailsTabContentProps) => {
  const { getPlanAvailabilityForEmployee, getMonthlyAllowanceForEmployee } = useSelfServeExplorerContext()
  const planAvailability = getPlanAvailabilityForEmployee(employee)
  const monthlyAllowance = getMonthlyAllowanceForEmployee(employee.id)

  const planCounts = planAvailability?.plan_counts

  const planTypes = []
  if (planCounts?.epo && planCounts.epo > 0) planTypes.push('EPO')
  if (planCounts?.hmo && planCounts.hmo > 0) planTypes.push('HMO')
  if (planCounts?.pos && planCounts.pos > 0) planTypes.push('POS')
  if (planCounts?.ppo && planCounts.ppo > 0) planTypes.push('PPO')

  const goldPlans = plans?.filter(plan => plan.plan.metal_tier === 'gold') || []
  const cheapestGoldPlan = goldPlans.sort((a, b) => a.total_premium.subunits - b.total_premium.subunits)[0]

  const bronzePlans = plans?.filter(plan => plan.plan.metal_tier === 'expanded_bronze' || plan.plan.metal_tier === 'bronze') || []
  const cheapestBronzePlan = bronzePlans.sort((a, b) => a.total_premium.subunits - b.total_premium.subunits)[0]

  let bronzePlanWidthPercentage = 100
  if (cheapestGoldPlan && cheapestBronzePlan) {
    const goldPlanCost = cheapestGoldPlan.total_premium.subunits / 100
    const bronzePlanCost = cheapestBronzePlan.total_premium.subunits / 100

    if (monthlyAllowance >= goldPlanCost) {
      // Fully covers gold plan - show full width
      bronzePlanWidthPercentage = 100
    } else if (monthlyAllowance >= bronzePlanCost) {
      // Covers bronze but not gold - scale by employer contribution percentage
      bronzePlanWidthPercentage = (monthlyAllowance / goldPlanCost) * 100
    } else {
      // Doesn't cover either - scale by plan cost ratio
      bronzePlanWidthPercentage = (bronzePlanCost / goldPlanCost) * 100
    }
  }

  return (
    <div className='space-y-6'>
      <div className='space-y-4'>
        {(cheapestGoldPlan || cheapestBronzePlan) && <h3 className='heading-sm mt-4'>How <span className='sensitive'>{employee.name}</span> could spend their {formatMoney(monthlyAllowance)} allowance</h3>}
        {cheapestGoldPlan && (
          <>
            <p className='body-bold'>If they have more significant medical needs</p>
            <div>
              <SpendBreakdownVisual
                allowanceAmount={monthlyAllowance}
                planCost={(cheapestGoldPlan?.total_premium?.subunits ?? 0) / 100}
                targetPlan={cheapestGoldPlan}
              />
            </div>
          </>
        )}
        {cheapestBronzePlan && (
          <>
            {cheapestGoldPlan && <div className='border-t border-color-stroke-light w-full' />}
            <p className='body-bold'>If they have minimal medical needs</p>
            <div>
              <SpendBreakdownVisual
                allowanceAmount={monthlyAllowance}
                planCost={(cheapestBronzePlan?.total_premium?.subunits ?? 0) / 100}
                targetPlan={cheapestBronzePlan}
                containerWidthPercentage={bronzePlanWidthPercentage}
                  // containerWidthPercentage={
                  //   cheapestGoldPlan && monthlyAllowance < cheapestGoldPlan.total_premium.subunits / 100
                  //     ? (monthlyAllowance / (cheapestGoldPlan.total_premium.subunits / 100)) * 100
                  //     : 100
                  // }
              />
            </div>
          </>
        )}
      </div>
    </div>
  )
}
